import { Link } from 'react-router-dom' //,useNavigate
import { useTranslation } from 'react-i18next'
import { useStore } from '../store/useStore'
import Layout from '../components/Layout'
import { useEffect, useState } from 'react'
import { api } from '../lib/api'

interface MatchSuggestion {
  id: number
  otherUser: {
    id: number
    username: string
    picture: string | null
    displayName: string | null
    country: string | null
    locale: string
    has_persona?: number
  }
  matchScore: number
  reason: string
}

function calculateProfileCompletion(profile: Record<string, unknown> | null, user: Record<string, unknown> | null): number {
  if (!profile && !user) return 0

  const fields = [
    { value: profile?.display_name || user?.username, weight: 10 },
    { value: profile?.bio, weight: 15 },
    { value: profile?.birth_year, weight: 10 },
    { value: profile?.gender, weight: 10 },
    { value: profile?.country, weight: 10 },
    { value: profile?.city, weight: 5 },
    { value: profile?.mbti, weight: 10 },
    { value: Array.isArray(profile?.interests) && profile?.interests.length > 0, weight: 15 },
    { value: Array.isArray(profile?.languages) && profile?.languages.length > 0, weight: 10 },
    { value: user?.picture, weight: 5 },
  ]

  const completed = fields.reduce((acc, field) => {
    if (field.value) return acc + field.weight
    return acc
  }, 0)

  return Math.min(completed, 100)
}

export default function HomePage() {
  const { t } = useTranslation()
  const { user, profile, myPersona, points, fetchPoints } = useStore()
  //const navigate = useNavigate()
  const profileCompletion = calculateProfileCompletion(profile as unknown as Record<string, unknown>, user as unknown as Record<string, unknown>)

  const [suggestion, setSuggestion] = useState<MatchSuggestion | null>(null)
  const [activeMatches, setActiveMatches] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        // Fetch today's suggestion
        const suggestionData = await api.matches.getSuggestion()
        if (suggestionData.suggestion) {
          setSuggestion(suggestionData.suggestion as MatchSuggestion)
        }

        // Fetch matches count
        const matchesData = await api.matches.list('recent')
        const matched = (matchesData.matches || []).filter((m: any) => m.status === 'matched').length
        setActiveMatches(matched)

        fetchPoints()
      } catch (error) {
        console.error('Failed to fetch home data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [fetchPoints])

  // useEffect(() => {
  //   // Redirect to profile page if profile is not complete
  //   if (profile && profileCompletion < 50) {
  //     navigate('/profile')
  //   }
  // }, [profile, profileCompletion, navigate])

  return (
    <Layout>
      <div className="max-w-xl mx-auto space-y-6">

        {/* Hero Section with Floating Blobs */}
        <div className="relative overflow-hidden rounded-3xl">
          {/* Decorative blobs */}
          <div className="floating-blob floating-blob-coral w-48 h-48 -top-10 -right-10" />
          <div className="floating-blob floating-blob-mint w-40 h-40 -bottom-10 -left-10" />
          <div className="floating-blob floating-blob-purple w-32 h-32 top-1/2 left-1/2" />

          <div className="glass-card p-6 relative z-10">
            <div className="flex items-center gap-4">
              {/* Premium Avatar */}
              <div className="avatar-premium flex-shrink-0">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-coral-300 to-mint-300">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.username} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white text-2xl font-bold">
                      {user?.username?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gradient-premium truncate">
                  {t('home.hello', { name: user?.username })} 👋
                </h1>
                <p className="text-sm text-base-content/60 mt-0.5">
                  {t('home.welcomeMessage', '오늘도 특별한 만남을 시작해보세요')}
                </p>
              </div>
            </div>

            {/* Points Display */}
            <div className="mt-4 flex items-center justify-between">
              <div className="points-badge">
                <span className="text-lg">🪙</span>
                <span>{points?.general_points?.toLocaleString() || 0}</span>
                <span className="text-xs text-amber-700/70">GP</span>
              </div>

              <Link to="/profile" className="text-xs text-primary hover:underline">
                {t('home.viewProfile', '프로필 보기')} →
              </Link>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 fade-in-up">
          <div className="stat-card hover-lift">
            <div className="stat-icon">
              {myPersona?.auto_respond_enabled ? '🤖' : '😴'}
            </div>
            <div className={`stat-value text-lg ${myPersona?.auto_respond_enabled ? 'text-success' : 'text-base-content/40'}`}>
              {myPersona?.auto_respond_enabled ? 'ON' : 'OFF'}
            </div>
            <div className="stat-label">{t('home.persona', '페르소나')}</div>
          </div>

          <div className="stat-card hover-lift">
            <div className="stat-icon">💝</div>
            <div className="stat-value text-secondary">{activeMatches}</div>
            <div className="stat-label">{t('home.activeMatches', '활성 매칭')}</div>
          </div>

          <div className="stat-card hover-lift">
            <div className="stat-icon">📊</div>
            <div className="stat-value text-primary">{profileCompletion}%</div>
            <div className="stat-label">{t('home.profileComplete', '프로필')}</div>
          </div>
        </div>

        {/* Today's Suggestion Card */}
        {suggestion && (
          <div className="suggestion-card fade-in-scale">
            <style>{`
              @keyframes pulse-glow {
                0%, 100% { opacity: 0.6; transform: scale(1); }
                50% { opacity: 1; transform: scale(1.1); }
              }
            `}</style>
            <div className="relative z-10">
              {/* Badge */}
              <div className="text-center mb-4">
                <span className="badge-premium inline-flex items-center gap-1.5 px-4 py-1.5">
                  <span className="text-base">✨</span>
                  {t('home.todaySuggestion', '오늘의 추천')}
                </span>
              </div>

              {/* Profile Preview */}
              <Link to={`/profile/${suggestion.otherUser.id}`} className="block">
                <div className="flex flex-col items-center">
                  <div className="relative mb-3">
                    <div className="avatar-premium avatar-ring-animated">
                      <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-coral-200 to-mint-200">
                        {suggestion.otherUser.picture ? (
                          <img src={suggestion.otherUser.picture} alt={suggestion.otherUser.username} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white text-3xl font-bold">
                            {suggestion.otherUser.username?.[0]?.toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Persona indicator - blue pulse (국가 플래그 대신) */}
                    {suggestion.otherUser.has_persona === 1 && (
                      <div
                        className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white"
                        style={{
                          background: '#3b82f6',
                          boxShadow: '0 0 10px rgba(59, 130, 246, 0.7)',
                          animation: 'pulse-glow 2s ease-in-out infinite',
                        }}
                        title="AI 자동응답 활성화됨"
                      />
                    )}
                  </div>

                  <h3 className="text-lg font-bold text-base-content">
                    {suggestion.otherUser.displayName || suggestion.otherUser.username}
                  </h3>
                  {/* reason은 국가 힌트가 포함될 수 있으므로 스테이지1에서는 숨김 */}

                  {/* Match Score */}
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-sm text-base-content/50">{t('home.matchScore', '매칭 점수')}</span>
                    <span className="text-xl font-bold text-gradient-coral">{Math.round(suggestion.matchScore)}%</span>
                  </div>
                </div>
              </Link>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <Link to="/matches" className="btn-premium flex-1 text-center py-3">
                  <span className="inline-flex items-center justify-center gap-2">
                    <span>💕</span>
                    {t('home.goToMatches', '매칭 확인하기')}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* No Suggestion - Show CTA */}
        {!loading && !suggestion && (
          <div className="glass-card p-6 text-center fade-in-up">
            <div className="text-5xl mb-4">💝</div>
            <h3 className="text-lg font-bold text-base-content mb-2">
              {t('home.noSuggestionTitle', '오늘의 추천을 기다리세요!')}
            </h3>
            <p className="text-sm text-base-content/60 mb-4">
              {t('home.noSuggestionDesc', '프로필을 완성하면 더 좋은 추천을 받을 수 있어요')}
            </p>
            {profileCompletion < 100 && (
              <Link to="/profile" className="btn-premium inline-flex items-center gap-2">
                <span>✏️</span>
                {t('home.completeProfile', '프로필 완성하기')}
              </Link>
            )}
          </div>
        )}

        {/* Feature Cards Grid */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* AI Persona Card */}
          <div className="premium-card card-gradient-imagine p-5 fade-in-up delay-100">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-imagine-400 to-imagine-600 flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-2xl">🤖</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-imagine-700">{t('home.myPersona', '내 페르소나')}</h3>
                  {myPersona?.auto_respond_enabled && (
                    <span className="badge badge-sm bg-imagine-500 text-white border-0">
                      {t('home.active', '활성')}
                    </span>
                  )}
                </div>
                <p className="text-xs text-base-content/60 mb-3">
                  {myPersona?.learning_status === 'ready'
                    ? t('home.personaReady', '오프라인일 때 자동 응답해요')
                    : t('home.personaSetup', '나를 대신하는 AI를 만들어보세요')
                  }
                </p>

                {myPersona?.personality_traits && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {myPersona.personality_traits.slice(0, 2).map((trait) => (
                      <span key={trait} className="badge badge-sm badge-outline border-imagine-300 text-imagine-700">
                        {trait}
                      </span>
                    ))}
                  </div>
                )}

                <Link
                  to="/my-persona"
                  className="btn btn-sm bg-gradient-to-r from-imagine-500 to-imagine-600 hover:from-imagine-600 hover:to-imagine-700 text-white border-0 rounded-full shadow-md"
                >
                  {myPersona?.learning_status === 'ready' ? t('home.settings', '설정') : t('home.create', '만들기')}
                </Link>
              </div>
            </div>
          </div>

          {/* Profile Completion Card */}
          <div className="premium-card card-gradient-mint p-5 fade-in-up delay-200">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-secondary to-mint-500 flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-2xl">👤</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-mint-700 mb-1">{t('home.myProfile', '내 프로필')}</h3>
                <p className="text-xs text-base-content/60 mb-3">
                  {profileCompletion >= 100
                    ? t('home.profileCompleted', '프로필이 완성되었어요!')
                    : t('home.profileNeedComplete', '더 좋은 매칭을 위해 완성하세요')
                  }
                </p>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-base-content/60 mb-1">
                    <span>{t('home.completion', '완성도')}</span>
                    <span>{profileCompletion}%</span>
                  </div>
                  <div className="progress-premium">
                    <div
                      className="progress-premium-bar"
                      style={{ width: `${profileCompletion}%` }}
                    />
                  </div>
                </div>

                <Link
                  to="/profile"
                  className="btn btn-sm bg-gradient-to-r from-secondary to-mint-600 hover:from-mint-600 hover:to-secondary text-white border-0 rounded-full shadow-md"
                >
                  {profileCompletion >= 100 ? t('home.view', '보기') : t('home.complete', '완성하기')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Active Chats Quick Access */}
        {activeMatches > 0 && (
          <Link
            to="/matches"
            className="premium-card p-4 flex items-center gap-4 hover-lift fade-in-up delay-300"
          >
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-coral-400 to-mint-400 flex items-center justify-center shadow-md">
              <span className="text-xl">💬</span>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-base-content">
                {t('home.activeChats', '진행 중인 대화')}
              </h3>
              <p className="text-sm text-base-content/60">
                {t('home.activeChatsCount', { count: activeMatches })}
              </p>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        )}

        {/* Tips Card */}
        <div className="glass-card p-4 fade-in-up delay-400">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-400 flex items-center justify-center shadow-sm flex-shrink-0">
              <span className="text-lg">💡</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-base-content">
                {t('home.tipTitle', 'AI 페르소나란?')}
              </p>
              <p className="text-xs text-base-content/60 mt-0.5">
                {t('home.tipDesc', '오프라인일 때 나의 성격과 말투로 자동 응답해주는 AI 분신이에요.')}
              </p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-8">
            <span className="loading loading-spinner loading-lg text-primary" />
          </div>
        )}
      </div>
    </Layout>
  )
}
